const OVERLAY = '[data-problem-report-overlay]'
const PRIVATE = 'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [data-sensitive]'
const RESOURCE_ELEMENTS = 'iframe, object, embed, video, audio, canvas, picture, source, svg, image, use, foreignObject, feImage, input[type="image"]'

function localUrl(value: string, doc: Document): boolean {
  try {
    const url = new URL(value, doc.baseURI)
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.origin === new URL(doc.URL).origin
  } catch { return false }
}

/** Initial-clone exclusion: no live DOM changes, no resource proxy. Responsive
 * pictures, SVG resource references and canvas/video are conservatively omitted. */
export function omitCaptureElement(element: Element): boolean {
  if (element.shadowRoot) return true
  if (element.closest(OVERLAY) || element.matches(RESOURCE_ELEMENTS)) return true
  if (element.matches('link') && element.getAttribute('rel') !== 'stylesheet') return true
  if (element instanceof HTMLImageElement) {
    return !!element.srcset || !localUrl(element.currentSrc || element.src, element.ownerDocument)
  }
  return false
}

function assertCssTextSafe(text: string, doc: Document): void {
  if (/@import\b|image-set\s*\(/i.test(text)) throw new Error('Capture unavailable for imported or image-set styles')
  for (const match of text.matchAll(/url\s*\(([^)]*)\)/gi)) {
    const raw = match[1].trim()
    const value = /^("[^"]*"|'[^']*')$/.test(raw) ? raw.slice(1, -1) : raw
    if (/[\\\s()"']/.test(value) || !localUrl(value, doc)) throw new Error('Capture unavailable for external or ambiguous CSS resources')
  }
}

function assertDeclarationsSafe(style: CSSStyleDeclaration, doc: Document): void {
  for (let index = 0; index < style.length; index++) {
    const value = style.getPropertyValue(style.item(index))
    // Escaped function/URL spellings must not bypass the simple resource check.
    if (value.includes('\\')) throw new Error('Capture unavailable for escaped CSS declarations')
    assertCssTextSafe(value, doc)
  }
}

/** Fail closed BEFORE the library mounts its iframe. onclone is too late to
 * prevent stylesheet fetches. This accepts only inspectable first-party CSS;
 * unknown resource syntax falls back to text/manual images. Same-origin server
 * redirects are outside this client proof; no universal network guarantee. */
export function assertCaptureResourcesSafe(doc: Document): void {
  for (const style of Array.from(doc.querySelectorAll('style'))) assertCssTextSafe(style.textContent ?? '', doc)
  const walk = (rules: CSSRuleList) => {
    for (const rule of Array.from(rules)) {
      assertCssTextSafe(rule.cssText, doc)
      if ('style' in rule) assertDeclarationsSafe((rule as CSSStyleRule).style, doc)
      if ('cssRules' in rule) walk((rule as CSSGroupingRule).cssRules)
    }
  }
  for (const sheet of Array.from(doc.styleSheets)) {
    if (sheet.href && !localUrl(sheet.href, doc)) throw new Error('Capture unavailable for external stylesheet')
    try { walk(sheet.cssRules) } catch { throw new Error('Capture unavailable for unsafe or unreadable stylesheet') }
  }
  for (const element of Array.from(doc.querySelectorAll<HTMLElement>('[style]'))) {
    if (!element.closest(OVERLAY)) assertDeclarationsSafe(element.style, doc)
  }
  // Linked styles without a readable sheet may still load during cloning.
  for (const link of Array.from(doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))) {
    if (!link.sheet || !localUrl(link.href, doc)) throw new Error('Capture unavailable while styles are loading')
  }
}

/** Runs on the clone before rasterization. Replaced-image identity and every
 * paint style are discarded, not merely covered by a background. This is a
 * defined-region mask, not a general PII detector or layout engine. */
export function maskCaptureDocument(doc: Document): void {
  for (const overlay of Array.from(doc.querySelectorAll(OVERLAY))) overlay.remove()
  for (const element of Array.from(doc.querySelectorAll<HTMLElement>(PRIVATE))) {
    if (element.parentElement?.closest(PRIVATE)) continue
    const rect = element.getBoundingClientRect()
    const computed = doc.defaultView?.getComputedStyle(element)
    const placeholder = doc.createElement('span')
    placeholder.setAttribute('data-report-masked', '')
    placeholder.style.setProperty('all', 'initial', 'important')
    // Preserve only surrounding layout; never copy class/id, source attributes,
    // content, background, border-image, filters, masks or other paint channels.
    for (const property of ['position', 'top', 'right', 'bottom', 'left', 'z-index',
      'margin-top', 'margin-right', 'margin-bottom', 'margin-left', 'vertical-align',
      'float', 'clear', 'order', 'align-self', 'justify-self', 'grid-area']) {
      const value = computed?.getPropertyValue(property)
      if (value) placeholder.style.setProperty(property, value, 'important')
    }
    const display = computed?.display
    const styles: Record<string, string> = {
      display: !display || display === 'inline' ? 'inline-block' : display,
      'box-sizing': 'border-box', width: `${rect.width}px`, height: `${rect.height}px`,
      'min-width': `${rect.width}px`, 'max-width': `${rect.width}px`,
      'min-height': `${rect.height}px`, 'max-height': `${rect.height}px`,
      'flex-grow': '0', 'flex-shrink': '0',
      background: '#000000', color: 'transparent', 'text-shadow': 'none',
      border: '0', 'border-image-source': 'none', content: 'none', opacity: '1',
      visibility: 'visible', overflow: 'hidden', appearance: 'none',
    }
    for (const [property, value] of Object.entries(styles)) placeholder.style.setProperty(property, value, 'important')
    element.replaceWith(placeholder)
  }
  const style = doc.createElement('style')
  style.textContent = '[data-report-masked]::before,[data-report-masked]::after{content:none!important;display:none!important}'
  doc.head.append(style)
}
